/**
 * Conversion destinations: one row per saved destination (switch, outcomes,
 * test code, test send, remove) and the "Add a destination" picker that
 * lists what the connected ads accounts offer.
 */
import { useEffect, useState } from "react";
import { Check, FlaskConical, Megaphone, Plus, Trash2 } from "lucide-react";
import { Button, Input, Modal, Skeleton, useConfirm, useToast } from "@/components/ui";
import { Card, Empty, dotClass, timeAgo } from "@/features/command/ui";
import { cn } from "@/lib/utils";
import {
  OUTCOMES, errText, eventsOf, plainDetail, platformInfo, trackingApi,
  type DiscoveredAccount, type Destination, type Outcome, type Send,
} from "./api";
import { StatusPill } from "./SendsLog";

export function Switch({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-indigo-500" : "bg-zinc-700",
      )}
    >
      <span className={cn("inline-block h-5 w-5 rounded-full bg-white shadow transition-transform", checked ? "translate-x-[22px]" : "translate-x-0.5")} />
    </button>
  );
}

export function DestinationRow({ d, onSaved, onRemoved, onTested }: {
  d: Destination;
  onSaved: (d: Destination) => void;
  onRemoved: (id: string) => void;
  /** A test went out: the counts and the send log are worth re-reading. */
  onTested?: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const info = platformInfo(d.platform);
  const events = eventsOf(d);
  const [code, setCode] = useState(d.test_code ?? "");
  const [busy, setBusy] = useState<null | "save" | "test" | "remove">(null);
  const [result, setResult] = useState<{ send: Send | null } | null>(null);
  useEffect(() => { setCode(d.test_code ?? ""); }, [d.test_code]);

  const save = async (patch: { enabled?: boolean; events?: Record<Outcome, boolean>; test_code?: string | null }, ok?: string) => {
    setBusy("save");
    onSaved({ ...d, ...patch }); // show it at once; put it back if the save fails
    try {
      const r = await trackingApi.save({ platform: d.platform, account_id: d.account_id, destination_id: d.destination_id, ...patch });
      onSaved({ ...d, ...r.destination, sent: d.sent, failed: d.failed, last_send_at: d.last_send_at });
      if (ok) toast.success(ok);
    } catch (err) {
      onSaved(d);
      toast.error("Couldn't save that", errText(err));
    } finally {
      setBusy(null);
    }
  };

  const saveCode = async () => {
    const next = code.trim() || null;
    if (next === (d.test_code ?? null)) return;
    await save({ test_code: next }, next ? "Test code saved" : "Test code removed");
  };

  const test = async () => {
    if (code.trim() && code.trim() !== (d.test_code ?? "")) await saveCode();
    setBusy("test");
    setResult(null);
    try {
      const r = await trackingApi.test(d.id);
      setResult(r);
      onTested?.();
    } catch (err) {
      toast.error("Couldn't send the test", errText(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!(await confirm({
      title: `Stop sending to ${d.name || info.label}?`,
      description: "It's removed from this list and nothing more goes there. Its past sends stay in the platform's own reports.",
      confirmLabel: "Remove",
      danger: true,
    }))) return;
    setBusy("remove");
    try {
      await trackingApi.remove(d.id);
      onRemoved(d.id);
      toast.success("Removed");
    } catch (err) {
      toast.error("Couldn't remove it", errText(err));
      setBusy(null);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", d.enabled ? dotClass(info.dot) : "bg-zinc-600")} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-semibold text-zinc-100">{info.label}</span>
            <span className="truncate text-sm text-zinc-300">{d.name || d.destination_id}</span>
          </div>
          <div className="mt-0.5 text-xs text-zinc-500">
            {info.noun} {d.destination_id}
            {" · "}
            <span className="tabular-nums">{d.sent} sent</span>
            {d.failed > 0 && <span className="tabular-nums text-rose-400"> · {d.failed} failed</span>}
            {d.last_send_at ? ` · last send ${timeAgo(d.last_send_at)}` : " · nothing sent yet"}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={cn("hidden text-xs font-medium sm:inline", d.enabled ? "text-emerald-400" : "text-zinc-500")}>{d.enabled ? "On" : "Off"}</span>
          <Switch
            checked={d.enabled}
            disabled={busy === "save"}
            label={d.enabled ? "Stop sending here" : "Start sending here"}
            onChange={(v) => void save({ enabled: v }, v ? `Sending to ${info.label} now` : `Stopped sending to ${info.label}`)}
          />
        </div>
      </div>

      <div className="mt-4 grid gap-4 border-t border-zinc-800/70 pt-4 lg:grid-cols-[auto_minmax(0,1fr)]">
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">What goes there</div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {OUTCOMES.map((o) => (
              <label key={o.key} className="flex cursor-pointer items-center gap-1.5 text-sm text-zinc-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-indigo-500"
                  checked={events[o.key]}
                  disabled={busy === "save"}
                  onChange={(e) => void save({ events: { ...events, [o.key]: e.target.checked } })}
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
        <div className="min-w-0">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Test code <span className="font-normal normal-case tracking-normal text-zinc-600">(optional, from the platform's test events screen)</span></div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-full sm:w-44">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onBlur={() => void saveCode()}
                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                placeholder="e.g. TEST12345"
                aria-label="Test code"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void test()}
              loading={busy === "test"}
              disabled={!code.trim() || busy === "remove"}
              title={code.trim() ? "Sends one signup marked as a test, so it isn't counted" : "Add the platform's test code first"}
              leftIcon={<FlaskConical className="h-3.5 w-3.5" />}
            >
              Send a test signup
            </Button>
            <Button variant="ghost" size="sm" onClick={() => void remove()} loading={busy === "remove"} leftIcon={<Trash2 className="h-3.5 w-3.5" />} className="text-zinc-400 hover:text-rose-300 sm:ml-auto">
              Remove
            </Button>
          </div>
          {result && <TestResult send={result.send} />}
        </div>
      </div>
    </Card>
  );
}

function TestResult({ send }: { send: Send | null }) {
  if (!send) {
    return (
      <p className="mt-2 rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-xs text-amber-300">
        Nothing was sent. This CRM isn't linked to SyncSuite services yet, so it can't reach the ad platforms.
      </p>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-xs">
      <StatusPill status={send.status} />
      <span className="min-w-0 text-zinc-400">{send.detail ? plainDetail(send.detail) : send.status === "sent" ? "Accepted by the platform." : ""}</span>
    </div>
  );
}

/** Pick a destination from the connected ads accounts. */
export function AddDestinationModal({ open, onClose, saved, onAdded }: {
  open: boolean;
  onClose: () => void;
  saved: Destination[];
  onAdded: (d: Destination) => void;
}) {
  const toast = useToast();
  const [data, setData] = useState<{ accounts: DiscoveredAccount[]; error?: string } | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setData(null);
    trackingApi.discover()
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err) => { if (!cancelled) setData({ accounts: [], error: errText(err) }); });
    return () => { cancelled = true; };
  }, [open]);

  const have = new Set(saved.map((d) => `${d.account_id}::${d.destination_id}`));
  const add = async (a: DiscoveredAccount, dest: { id: string; name: string }) => {
    const key = `${a.accountId}::${dest.id}`;
    setAdding(key);
    try {
      const r = await trackingApi.save({ platform: a.platform, account_id: a.accountId, destination_id: dest.id, name: dest.name, enabled: true });
      onAdded({ ...r.destination, sent: 0, failed: 0, last_send_at: null });
      toast.success(`Added ${dest.name}`, `New signups, trial starts and payments now go to ${platformInfo(a.platform).label}.`);
      onClose();
    } catch (err) {
      toast.error("Couldn't add it", errText(err));
    } finally {
      setAdding(null);
    }
  };

  const accounts = data?.accounts ?? [];
  return (
    <Modal open={open} onClose={onClose} title="Add a destination" description="Pick where your results should go. Each ads account lists what it can receive." size="lg">
      {!data ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : accounts.length === 0 ? (
        <Empty icon={Megaphone} title="No ads account connected">
          Connect an ads account in SyncSuite (Apps → Social & ads) and it shows up here.
          {data.error && <span className="mt-2 block text-zinc-600">{data.error}</span>}
        </Empty>
      ) : (
        <div className="space-y-4">
          {accounts.map((a) => {
            const info = platformInfo(a.platform);
            return (
              <div key={a.accountId}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span className={cn("h-2 w-2 rounded-full", dotClass(info.dot))} />
                  <span className="text-sm font-semibold text-zinc-100">{info.label}</span>
                  {a.name && <span className="truncate text-sm text-zinc-400">{a.name}</span>}
                </div>
                {a.error ? (
                  <p className="rounded-lg border border-zinc-800 px-3 py-2 text-xs text-zinc-500">Couldn't list what this account offers: {a.error}</p>
                ) : a.destinations.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-zinc-800 px-3 py-2 text-xs text-zinc-500">
                    This account has no {info.noun.toLowerCase()} yet. Create one in {info.label}, then come back.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {a.destinations.map((dest) => {
                      const key = `${a.accountId}::${dest.id}`;
                      const added = have.has(key);
                      return (
                        <div key={dest.id} className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-zinc-100">{dest.name}</div>
                            <div className="truncate text-[11px] text-zinc-500">{info.noun} {dest.id}{dest.status ? ` · ${dest.status.toLowerCase()}` : ""}</div>
                          </div>
                          {added ? (
                            <span className="flex items-center gap-1 text-xs font-medium text-emerald-400"><Check className="h-3.5 w-3.5" />Added</span>
                          ) : (
                            <Button size="sm" onClick={() => void add(a, dest)} loading={adding === key} disabled={!!adding && adding !== key} leftIcon={<Plus className="h-3.5 w-3.5" />}>Add</Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
